import { useState, useMemo, useEffect } from "react";
import AssetSelector from "components/core/blocks/AssetSelector/AssetSelector";
import { Col, Row } from "react-bootstrap";
import marketPlace from "redux/reducers/marketPlace";
import Loading from "components/core/blocks/Loading";
import ImageGenerationDialogue from "./ImageGenerationDialogue";
import { useMutation } from "@tanstack/react-query";
import { useFetch } from "hooks/api";
import api from "utils/api";
import moment from "moment";
import PendingRequestsTable from "./PendingRequestsTable";
import { FaTriangleExclamation } from "react-icons/fa6";

const ImageGeneration = ({ productId, asin }) => {
  const [productAssetData, setProductAssetData] = useState([]);

  const defaultFilters = useMemo(() => {
    return { tags: `${asin.toLowerCase()}, trellis generative-ai ${asin.toLowerCase()}` };
  }, [asin]);

  const { data: imageGenRequestData, isLoading: isImageGenRequestDataLoading } = useFetch(
    ["imageGenRequests"],
    "/api/genai",
    {},
    {
      select: (d) => d.data.results,
    },
  );

  const { data: imageGenTaskData, isLoading: isImageGenTaskDataLoading } = useFetch(
    ["imageGenTasks"],
    "/api/genai/results",
    {},
    {
      select: (d) => d.data.results,
    },
  );

  const productImageGenerationRequests = useMemo(
    () =>
      imageGenRequestData?.filter((request) => {
        return request.gvproduct === parseInt(productId);
      }),
    [imageGenRequestData, productId],
  );

  const tags = useMemo(() => {
    const tagList = [`trellis generative-ai ${asin.toLowerCase()}`, `${asin.toLowerCase()}`];

    if (productImageGenerationRequests?.length) {
      productImageGenerationRequests.forEach((request) => {
        if (request.name) tagList.push(`trellis generative-ai ${asin.toLowerCase()} ${request.name}`);
      });
    }
    return tagList;
  }, [productImageGenerationRequests, asin]);

  const hasRejectedImageGenerationRequest = productImageGenerationRequests?.some(
    (request) => request.state === "REJECTED",
  );

  const combinedData = useMemo(() => {
    return productAssetData.map((asset) => {
      const imageGenPotentialTaskIds = asset.tags.filter((t) => parseInt(t));
      const correspondingImageGenTask = imageGenTaskData?.find((task) =>
        imageGenPotentialTaskIds.includes(task.id.toString()),
      );

      const { prompt, theme } = correspondingImageGenTask ?? {};

      return { ...asset, prompt, theme, asin };
    });
  }, [productAssetData, imageGenTaskData, asin]);

  const getProductAssetsFromAmazon = useMutation({
    mutationFn: async () => {
      const body = {
        product: productId,
        search_parameters: {
          sortCriteria: {
            field: "CREATED_TIME",
            order: "DESC",
          },
          pageCriteria: {
            size: 500,
          },
          filterCriteria: {
            valueFilters: [
              {
                values: tags,
                valueField: "TAG",
              },
              {
                values: [`${asin.toUpperCase()}`],
                valueField: "ASIN",
              },
            ],
            rangeFilters: [],
          },
        },
      };
      return await api.post("/genai/search_amazon_assets/", body);
    },

    onSuccess: (res) => {
      const formattedData = JSON.parse(res.data.response);
      setProductAssetData(
        formattedData.assetList?.map((asset) => ({
          ...asset,
          file_meta_data: {},
          url_default: asset.storageLocationUrls.defaultUrl,
          tags: [
            ...asset.tags.filter((tag) => !moment(tag).isSame(moment.unix(asset.creationTime / 1000), "day")),
            moment.unix(asset.creationTime / 1000).format("MMMM DD, YYYY"),
          ],
        })),
      );
    },
  });

  useEffect(() => {
    getProductAssetsFromAmazon.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  return (
    <Row>
      <Col xs={12}>
        <h3 className="text-center" style={{ fontWeight: 400 }}>
          AI Image Generation
        </h3>

        {getProductAssetsFromAmazon.isPending || isImageGenRequestDataLoading || isImageGenTaskDataLoading ?
          <Loading height={"300px"} />
        : <>
            {hasRejectedImageGenerationRequest ?
              <Row>
                <p className="justify-content-center fs-4 d-flex align-items-center my-4">
                  <FaTriangleExclamation className="me-2" /> This product has been rejected by Amazon for image
                  generation because it belongs to an ineligible Amazon category.
                </p>
                <p className="text-center fs-4 mb-5">
                  If you believe this product category is no longer restricted, please reach out to Trellis support.
                </p>
              </Row>
            : productImageGenerationRequests?.length ?
              <div className="text-center">
                <h4 className="fs-4" style={{ fontWeight: 500 }}>
                  Image Generation Request{productImageGenerationRequests?.length > 1 ? "s" : ""}{" "}
                </h4>
                <Row className="d-flex justify-content-center">
                  <Col xs={12} lg={{ span: 7 }}>
                    <PendingRequestsTable
                      requestsData={productImageGenerationRequests}
                      isRequestsDataLoading={isImageGenRequestDataLoading}
                      asin={asin}
                      productId={productId}
                    />
                  </Col>
                </Row>
              </div>
            : null}
          </>
        }
        {hasRejectedImageGenerationRequest ?
          null
        : productAssetData?.length ?
          <>
            <ImageGenerationDialogue asin={asin} productId={productId} />
            <AssetSelector
              overrideAssetData={combinedData}
              marketPlace={marketPlace}
              hideSelectionControls={true}
              hidePreviewField={true}
              hideAssetTypeFilter={true}
              hideEligibilityFilter={true}
              hideTagsFilter={true}
              hideAdvancedFilters={true}
              hideGeneratedAssetsFilter={true}
              defaultPageSize={500}
              defaultFilters={defaultFilters}
              isImageGenerationSelector={true}
              initialTypeFilter={"LIFESTYLE_IMAGE"}
            />
          </>
        : getProductAssetsFromAmazon.isPending || isImageGenRequestDataLoading || isImageGenTaskDataLoading ?
          null
        : <Col className="pb-5 mx-auto" xs={12} lg={6}>
            {!productImageGenerationRequests?.length ?
              <p className="fs-standard text-center py-3">
                There are currently no assets generated for this product. Click the button below to start generating
                assets that can be used in Sponsored Brand media plans leveraging the power of AI.
              </p>
            : <p className="fs-standard text-center pb-3 pt-5">
                Images are currently being generated. Please check back soon to see the results.
              </p>
            }
            <ImageGenerationDialogue asin={asin} productId={productId} />
          </Col>
        }
      </Col>
    </Row>
  );
};

export default ImageGeneration;
